(ns app.front.view.property.header
  (:require [app.domain.property.model :as property-model]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :refer [for-frag]]
            [app.front.view.common.data-grid :as dg]
            [app.front.view.common.header :as header-common]
            [app.front.view.workspace.map :as workspace-map]
            [app.front.view.workspace.property-subs :as property-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-sub ::full-property-info-visible?
  #(::full-property-info-visible? % (header-common/full-header-info-by-default?)))


(rf/reg-event-db ::toggle-full-property-info-visibility
  (fn [db]
    (let [db-value  (::full-property-info-visible? db)
          ls-value  (header-common/full-header-info-by-default?)
          new-value (not (if (nil? db-value) ls-value db-value))]
      (header-common/set-full-header-info-default new-value)
      (-> db
          (assoc ::full-property-info-visible? new-value)))))


(defn prop-info [label content & [always-visible?]]
  (let [full-prop-info-visible? (<sub [::full-property-info-visible?])]
    (when (and content (or always-visible? full-prop-info-visible?))
      [dg/grid-item-tall label [:p content]])))


(defn address-with-municipality-name [tr property]
  (let [lang (<sub [:language])]
    (->> [(or (property-model/full-address property)
              (tr [:address/address-missing]))
          (property-model/municipality-name lang property)]
         (remove nil?)
         (str/join ", "))))


(defn map-wrapper [property]
  (let [location        (-> property :entity/location)
        layers          (<sub [::municipality/municipal-layers [:property/municipality property] false])
        candidate-point (<sub [::property-subs/candidate-point])
        primary-point   (when location
                          [(-> location :location.center/x)
                           (-> location :location.center/y)])
        map-center      (or candidate-point
                            primary-point)
        properties      (<sub [::property-subs/additional-property-geometries])]
    (when (and layers map-center)
      [workspace-map/workspace-map
       (merge-with merge
                   {:center              map-center
                    :layers              layers
                    :extended?           true
                    :primaryMapMarkers   [primary-point]
                    :secondaryMapMarkers (->> [(<sub [::property-subs/candidate-point])]
                                              (remove nil?))
                    :propertyGeometries  {:primaryPropertyGeometry      (property-model/geojson-feature
                                                                          (-> location :location/shape))
                                          :additionalPropertyGeometries (property-model/geojson-feature-collection
                                                                          (->> properties
                                                                               (filter #(= (:type %) "Polygon"))
                                                                               (map property-model/geojson-feature)))
                                          :roadSegmentGeometries        (property-model/geojson-feature-collection
                                                                          (->> properties
                                                                               (filter #(not= (:type %) "Polygon"))
                                                                               (map property-model/geojson-feature)))}})])))


(defn action-row [tr property full-prop-info-visible?]
  [:div
   [:div.flex.flex-row.justify-between
    [:div.flex.flex-col
     [:div.flex.flex-row.space-x-2
      (when (-> property :entity/location)
        [header-common/toggle-map-or-property-data-button (util/mmap {:map-view-id (:db/id property)}
                                                                     tr)])
      (let [label (tr [(if full-prop-info-visible? :workspace/hide-full-data :workspace/show-full-data)])]
        (cc/button {:kind             :primary
                    :size             :small
                    :icon             (if full-prop-info-visible? i/remove i/documents)
                    :class-name       (if full-prop-info-visible? "bg-black" "")
                    :icon-position    :right
                    :icon-description label
                    :on-click         #(>evt [::toggle-full-property-info-visibility])}
                   label))]]]

   [ui-sep/horizontal]])


(defn property-data [tr {:property/keys [id
                                         type
                                         secondary-id
                                         integration-id
                                         legal
                                         type-code
                                         zoning
                                         ward
                                         legacy-attributes
                                         last-updated-from-gis]
                         :as            property}]
  (let [property-description (when (-> property :property/from-st)
                               (tr [:property/from-to]
                                   [(:property/from-st property)
                                    (:property/to-st property)]))
        not-road?            (not= type :property.type/road)]
    [dg/data-grid
     (when not-road?
       [prop-info (tr [:property/id])
        id
        true])
     [prop-info
      (tr [:property/secondary-id])
      secondary-id]
     [prop-info
      (tr [:property/integration-id])
      integration-id]
     [prop-info
      (tr [:property/description])
      property-description]
     (when not-road?
       [prop-info
        (tr [:property/legal])
        legal
        true])
     [prop-info
      (tr [:property/type-code])
      type-code]
     [prop-info
      (tr [:property/zoning])
      zoning]
     [prop-info
      (tr [:property/ward])
      ward]
     (when (inst? last-updated-from-gis)
       [prop-info
        (tr [:property/updated-from-gis])
        ((<sub [::date-util/date-time-fn]) last-updated-from-gis)])
     (for-frag [attr legacy-attributes]
       ^{:key (:db/id attr)}
       [prop-info nil (:legacy-property-attribute/value attr)])]))


(defn header [tr property]
  (r/with-let [*address-ref       (r/atom nil)
               *address-min-width (r/atom 200)]
    (let [description             (property-model/property-type-text tr property)
          map-visible?            (<sub [::header-common/map-visible? (:db/id property)])
          full-prop-info-visible? (<sub [::full-property-info-visible?])]
      (when-let [new-width (some-> @*address-ref .-offsetWidth)]
        (when (> new-width @*address-min-width)
          (reset! *address-min-width new-width)))
      [:div.mb-8
       [:div.flex.flex-row [:h2 (address-with-municipality-name tr property)]]
       [:div.flex.flex-row.my-2 description]

       [action-row tr property full-prop-info-visible?]

       (if map-visible?
         [:div.flex.flex-col
          [:div.map-boundary.mb-4.md:mb-0.mr-6.w-full.bg-gray-400.flex.flex-shrink-0.items-center.justify-center.map-extended
           [map-wrapper property]]]

         [:div
          [:div.flex.flex-col
           (when (not (property-model/gis-verified? property))
             [:div.mb-2
              [ui/tag {:color      :blue
                       :type       :round
                       :class-name "ml-0"}
               (str/upper-case (tr [:property/data-source-user]))]])
           [property-data tr property]]])])))
